import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SearchResult } from '../../../models/search-result';
import { SearchService } from '../../../services/search.service';
import { RectService } from '../../../services/rect.service';
import { ChapterService } from '../../../services/chapter.service';
import _ from 'lodash';
import { Rect } from '../../../models/rect';
import { Chapter } from '../../../models/chapter';
import { PageService } from '../../../services/page.service';
import { SearchHistory } from '../../../models/search-history';
import { KeyValues } from '../../../utils/key-values';
import { environment } from '../../../../environments/environment';
import { StripHtmlPipe } from '../../../pipes/strip-html.pipe';
import { SidebarContentComponent } from '../sidebar/sidebar-content-component';
import { SearchItemComponent } from './search-item/search-item.component';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'sidebar-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    standalone: true,
    imports: [
        SearchItemComponent,
        SearchItemComponent,
        SearchItemComponent,
        SearchItemComponent,
        FormsModule,
        DatePipe
    ]
})
export class SearchComponent implements OnInit, OnDestroy, SidebarContentComponent {

    searchResults: Array<SearchResult>;
    allNotes: Array<Rect>;
    allChapter: Array<Chapter>;
    noteResults: Array<SearchResult>;
    chapterResults: Array<SearchResult>;
    quickLinkResults: Array<SearchResult>;
    searchHistory: Array<SearchHistory>;
    searchText = '';

    showTexts = environment.searchBorders.texts.min <= this.searchText.length
        && this.searchText.length <= environment.searchBorders.texts.max;
    showExercises = environment.searchBorders.exercises.min <= this.searchText.length
        && this.searchText.length <= environment.searchBorders.exercises.max;

    @ViewChild('searchField') searchField: ElementRef;

    timer = null;

    constructor(private rectService: RectService,
                private chapterService: ChapterService,
                private searchService: SearchService,
                private pageService: PageService) {
    }

    ngOnInit(): void {
        this.loadNotes();
        this.loadChapters();
        this.loadHistory();
        this.focusTextField();
    }

    ngOnDestroy(): void {
        this.searchResults = [];
        this.noteResults = [];
        this.chapterResults = [];
        this.quickLinkResults = [];
    }

    /**
     * Call api to get search results for search text / query
     *
     * @return void
     */
    searchQuery(): void {

        this.searchService.clearResults();
        this.searchResults = [];
        this.noteResults = [];
        this.chapterResults = [];
        this.quickLinkResults = [];

        if (this.searchText.length < 1) {
            this.loadHistory();

            return;
        }

        this.searchService.findQuickLinks(this.searchText).then(value => {
            this.quickLinkResults = value;
        });
        this.searchResults = [];

        this.searchService.findPageTexts(this.searchText).then((value => {
            this.searchResults = value;
        }));
        this.quickLinkResults = [];

        this.searchNotes();
        this.searchChapters();

    }

    /**
     * event handler (for keyboard timeout)
     *
     * @param event
     */
    keyUp(event): void {
        this.showTexts = environment.searchBorders.texts.min <= this.searchText.length
            && this.searchText.length <= environment.searchBorders.texts.max;
        this.showExercises = environment.searchBorders.exercises.min <= this.searchText.length
            && this.searchText.length <= environment.searchBorders.exercises.max;
        if (KeyValues.getKey(event) === 'Enter' && this.searchText !== '') {
            this.enter();

            return;
        }

        const timeout = 300;

        if (this.timer != null) {
            clearTimeout(this.timer);
            this.timer = null;
        }

        this.timer = setTimeout(() => {
            this.searchQuery();
        }, timeout);
    }

    /**
     * called for storing the search text
     */
    store(): void {
        this.searchService.storeSearch(this.searchText.trim());
    }

    /**
     * called on enter or search button clicked
     */
    enter(): void {
        this.store();
        this.searchQuery();
    }

    /**
     * loads all rect notes of the book
     */
    loadNotes(): void {
        this.rectService.getRects().then(rects => {
            this.allNotes = rects;
        });
    }

    /**
     * loads all chapters of the book
     */
    loadChapters(): void {
        this.chapterService.getChapters().then(chapter => {
            this.allChapter = chapter;
        });
    }

    /**
     * loads search history of the user
     */
    loadHistory(): void {
        this.searchHistory = this.searchService.getSearchHistory();
    }

    /**
     * filters the notes of the book by the search query
     */
    searchNotes(): void {
        this.noteResults = _.map(this.allNotes, note => {
            const stripHtmlPipe = new StripHtmlPipe();
            const result = new SearchResult();
            result.pageId = note.pageId;
            result.text = stripHtmlPipe.transform(note.noteText);
            result.position = note.position;

            return result;
        });

        this.noteResults = _.filter(this.noteResults, note => {
            return note.text && note.text.toLowerCase().indexOf(this.searchText.toLowerCase()) >= 0;
        });

        this.noteResults = _.sortBy(this.noteResults, ele => {
            return ele.pageId;
        });
    }

    /**
     * filters the chapters of the book by the search query
     */
    searchChapters(): void {
        this.chapterResults = _.map(this.allChapter, chapter => {
            const result = new SearchResult();
            result.pageId = this.pageService.getPageIdByIndex(chapter.index);
            result.text = chapter.title;
            result.chapter = chapter.sortOrder;

            return result;
        });

        this.chapterResults = _.filter(this.chapterResults, chapter => {
            return (chapter.text && chapter.text.toLowerCase().indexOf(this.searchText.toLowerCase()) >= 0)
                || (chapter.chapter && chapter.chapter.toString().toLowerCase().indexOf(this.searchText.toLowerCase()) >= 0);
        });

        this.chapterResults = _.sortBy(this.chapterResults, ele => {
            return ele.pageId;
        });
    }

    /**
     * calculates maximum height per search result container (in %)
     *
     * @returns {number}
     */
    getHeight(): number {
        return 100 / this.getContainerCount();
    }

    /**
     * sets focus to search text field
     */
    focusTextField(): void {
        this.searchService.setSearchField(this.searchField);
        this.searchText = this.searchService.getSearchText();
        this.searchService.clearResults();
        this.searchQuery();

        setTimeout(() => {
            $(this.searchField.nativeElement).focus().select();
        }, 300);
    }

    /**
     * fill text with text of given history entry
     *
     * @param {SearchHistory} history
     */
    fillSearchField(history: SearchHistory): void {
        this.searchText = history.text;
        this.searchQuery();
        this.searchService.storeSearch(history.text);
    }

    /**
     * calculates the number of search result containers
     *
     * @returns {number}
     */
    private getContainerCount(): number {
        let res = 1;

        if (this.searchResults && this.searchResults.length > 0) {
            res++;
        }
        if (this.quickLinkResults && this.quickLinkResults.length > 0) {
            res++;
        }
        if (this.chapterResults && this.chapterResults.length > 0) {
            res++;
        }

        return res;
    }
}
